import type { ThemeVariables } from './contexts/ThemeVariablesContext';

import { BRANDING_STORAGE_URL } from './config';
import { getCurrentSubdomain } from '@utils/subdomain';

export interface BrandingLogo {
  data: string;
  mimeType: string;
}

declare global {
  interface Window {
    __safBrandingLogos?: {
      light: BrandingLogo | null;
      dark: BrandingLogo | null;
    };
  }
}

function setDocumentColorScheme(mode: 'light' | 'dark') {
  const targets = [document.documentElement, document.body, document.getElementById('root')].filter(
    Boolean,
  ) as HTMLElement[];
  for (const target of targets) {
    if (target.getAttribute('data-mantine-color-scheme') !== mode) {
      target.setAttribute('data-mantine-color-scheme', mode);
    }
  }
}

const DEFAULT_THEME = {
  fonts: {
    body: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    heading: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Fira Code', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  light: {
    bg: '#ffffff',
    surface: '#ffffff',
    surfaceMuted: '#f3f4f6',
    text: '#0f172a',
    textMuted: '#64748b',
    border: '#e5e7eb',
    primary: '#228be6',
    primaryHover: '#1864ab',
    onPrimary: '#ffffff',
    danger: '#b91c1c',
    success: '#16a34a',
  },
  dark: {
    bg: '#0b1220',
    surface: '#0f172a',
    surfaceMuted: '#111827',
    text: '#f8fafc',
    textMuted: '#cbd5e1',
    border: '#334155',
    primary: '#60a5fa',
    primaryHover: '#93c5fd',
    onPrimary: '#0b1220',
    danger: '#f87171',
    success: '#4ade80',
  },
} satisfies ThemeVariables;

function mergeTheme<T extends Record<string, string>>(source: Partial<T> | undefined | null, fallback: T): T {
  const result: T = { ...fallback };
  for (const key of Object.keys(fallback) as (keyof T)[]) {
    const value = source?.[key];
    result[key] = typeof value === 'string' && value.trim().length > 0 ? (value as T[keyof T]) : fallback[key];
  }
  return result;
}

export function toRgb(hex: string): string {
  if (typeof hex !== 'string') {
    return '34, 139, 230';
  }
  const trimmed = hex.trim();
  if (!trimmed.startsWith('#')) {
    return '34, 139, 230';
  }
  const raw = trimmed.slice(1);
  const normalized = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (normalized.length !== 6) {
    return '34, 139, 230';
  }
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) {
    return '34, 139, 230';
  }
  return `${r}, ${g}, ${b}`;
}

function detectMode(): 'light' | 'dark' {
  const attr =
    document.documentElement.getAttribute('data-mantine-color-scheme') ||
    (document.body && document.body.getAttribute('data-mantine-color-scheme')) ||
    (document.getElementById('root') &&
      document.getElementById('root')?.getAttribute('data-mantine-color-scheme'));
  if (attr === 'dark' || attr === 'light') {
    return attr;
  }
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function applyThemeToDocument(processedTheme: ThemeVariables, isFromCache: boolean) {
  const root = document.documentElement;
  type ThemeColors = ThemeVariables['light'];

  const applyMode = (mode: 'light' | 'dark', colors: ThemeColors) => {
    root.style.setProperty(`--saf-color-bg-${mode}`, colors.bg);
    root.style.setProperty(`--saf-color-surface-${mode}`, colors.surface);
    root.style.setProperty(`--saf-color-surface-muted-${mode}`, colors.surfaceMuted);
    root.style.setProperty(`--saf-color-text-${mode}`, colors.text);
    root.style.setProperty(`--saf-color-text-muted-${mode}`, colors.textMuted);
    root.style.setProperty(`--saf-color-border-${mode}`, colors.border);
    root.style.setProperty(`--saf-color-primary-${mode}`, colors.primary);
    root.style.setProperty(`--saf-color-primary-hover-${mode}`, colors.primaryHover);
    root.style.setProperty(`--saf-color-on-primary-${mode}`, colors.onPrimary);
    root.style.setProperty(`--saf-color-danger-${mode}`, colors.danger);
    root.style.setProperty(`--saf-color-success-${mode}`, colors.success);
    root.style.setProperty(`--saf-color-primary-rgb-${mode}`, toRgb(colors.primary));
    root.style.setProperty(`--saf-color-danger-rgb-${mode}`, toRgb(colors.danger));
    root.style.setProperty(`--saf-color-success-rgb-${mode}`, toRgb(colors.success));
  };

  const applyActive = (mode: 'light' | 'dark') => {
    setDocumentColorScheme(mode);
    const colors = mode === 'dark' ? processedTheme.dark : processedTheme.light;
    const primaryRgb = toRgb(colors.primary);
    const dangerRgb = toRgb(colors.danger);
    const successRgb = toRgb(colors.success);

    root.style.setProperty('--saf-color-bg', colors.bg);
    root.style.setProperty('--saf-color-surface', colors.surface);
    root.style.setProperty('--saf-color-surface-muted', colors.surfaceMuted);
    root.style.setProperty('--saf-color-text', colors.text);
    root.style.setProperty('--saf-color-text-muted', colors.textMuted);
    root.style.setProperty('--saf-color-border', colors.border);
    root.style.setProperty('--saf-color-primary', colors.primary);
    root.style.setProperty('--saf-color-primary-hover', colors.primaryHover);
    root.style.setProperty('--saf-color-on-primary', colors.onPrimary);
    root.style.setProperty('--saf-color-danger', colors.danger);
    root.style.setProperty('--saf-color-success', colors.success);
    root.style.setProperty('--saf-color-primary-rgb', primaryRgb);
    root.style.setProperty('--saf-color-danger-rgb', dangerRgb);
    root.style.setProperty('--saf-color-success-rgb', successRgb);

    root.style.setProperty('--mantine-color-default-border', colors.border);
    root.style.setProperty('--mantine-color-body', colors.bg);
    root.style.setProperty('--mantine-color-default', colors.surface);
    root.style.setProperty('--mantine-color-text', colors.text);
    root.style.setProperty('--mantine-color-dimmed', colors.textMuted);

    root.style.setProperty('--mantine-color-blue-6', colors.primary);
    root.style.setProperty('--mantine-color-blue-7', colors.primaryHover);
    root.style.setProperty('--mantine-color-blue-8', colors.primary);
    root.style.setProperty('--mantine-color-blue-9', colors.primaryHover);
    root.style.setProperty('--mantine-color-blue-5', colors.primary);
    root.style.setProperty('--mantine-color-blue-4', colors.primary);
    root.style.setProperty(`--mantine-color-blue-3`, `rgba(${primaryRgb}, 0.35)`);
    root.style.setProperty(`--mantine-color-blue-1`, `rgba(${primaryRgb}, 0.12)`);
    root.style.setProperty(`--mantine-color-blue-0`, `rgba(${primaryRgb}, 0.08)`);
    root.style.setProperty(`--mantine-color-blue-light`, `rgba(${primaryRgb}, 0.12)`);
  };

  applyMode('light', processedTheme.light);
  applyMode('dark', processedTheme.dark);

  const preferredColorScheme = processedTheme.preferredColorScheme;

  // Apply preferred color scheme only on first load (not from cache)
  if (!isFromCache && preferredColorScheme) {
    applyActive(preferredColorScheme);
    try {
      localStorage.setItem('mantine-color-scheme-value', preferredColorScheme);
    } catch {
      // localStorage may be unavailable
    }
  } else {
    const detectedMode = detectMode();
    applyActive(detectedMode);
  }
}

function processRawTheme(theme: any): ThemeVariables {
  const preferredColorScheme = theme.preferredColorScheme === 'light' || theme.preferredColorScheme === 'dark'
    ? theme.preferredColorScheme
    : null;

  return {
    fonts: mergeTheme(theme.fonts ?? undefined, DEFAULT_THEME.fonts),
    light: mergeTheme(theme.light ?? undefined, DEFAULT_THEME.light),
    dark: mergeTheme(theme.dark ?? undefined, DEFAULT_THEME.dark),
    preferredColorScheme,
  };
}

export function extractBrandingProfileIdFromUploadId(): number | null {
  const path = window.location.pathname || '';
  const segments = path.split('/').filter(Boolean);
  if (!(segments.length >= 2 && segments[0] === 'd')) {
    return null;
  }

  const uploadId = decodeURIComponent(segments[1] || '');
  if (!uploadId || uploadId.length < 8) {
    return null;
  }

  const licenseChar = uploadId[4];
  if (!licenseChar || 'afpeo'.indexOf(licenseChar) === -1) {
    return null;
  }

  let cursor = 5;
  let digits = '';
  while (cursor < uploadId.length) {
    const ch = uploadId[cursor];
    if (ch >= '0' && ch <= '9') {
      digits += ch;
      cursor += 1;
      continue;
    }
    break;
  }

  if (!digits || cursor >= uploadId.length || uploadId[cursor] !== 'b') {
    return null;
  }

  const brandingProfileId = Number.parseInt(digits, 10);
  if (!brandingProfileId || brandingProfileId <= 0) {
    return null;
  }

  return brandingProfileId;
}

function loadBrandingForProfile(brandingProfileId: number) {
  const cacheKey = `branding_profile_${brandingProfileId}`;

  // Check localStorage cache first - apply immediately, then refetch for updates
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.theme) {
        const processedTheme = processRawTheme(parsed.theme);
        window.__safBrandingTheme = processedTheme;
        window.__safBrandingLogos = {
          light: parsed.logoLight ?? null,
          dark: parsed.logoDark ?? null,
        };
        window.dispatchEvent(new CustomEvent('saf-branding-theme-loaded', { detail: processedTheme }));
        applyThemeToDocument(processedTheme, true);
      }
    }
  } catch {
    // Ignore cache errors
  }

  // Fetch theme and logos from public storage (always fetch to get updates)
  Promise.all([
    fetch(`${BRANDING_STORAGE_URL}/b/${brandingProfileId}/theme.json`).then(r => r.ok ? r.json() : null).catch(() => null),
    fetch(`${BRANDING_STORAGE_URL}/b/${brandingProfileId}/logolight.json`).then(r => r.ok ? r.json() : null).catch(() => null),
    fetch(`${BRANDING_STORAGE_URL}/b/${brandingProfileId}/logodark.json`).then(r => r.ok ? r.json() : null).catch(() => null),
  ])
    .then(([theme, logoLight, logoDark]) => {
      if (theme) {
        // Cache theme and logos to localStorage
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ theme, logoLight, logoDark }));
        } catch {
          // localStorage may be unavailable
        }

        const processedTheme = processRawTheme(theme);

        window.__safBrandingTheme = processedTheme;
        window.__safBrandingLogos = {
          light: logoLight ?? null,
          dark: logoDark ?? null,
        };
        window.dispatchEvent(new CustomEvent('saf-branding-theme-loaded', { detail: processedTheme }));
        applyThemeToDocument(processedTheme, false);
      }
    })
    .catch(() => {
      // Ignore fetch errors - theme will fall back to default
    });
}

import { getSubdomainBrandingProfile } from '@services/api';

function loadBrandingForSubdomain(subdomain: string) {
  const subdomainCacheKey = `subdomain_branding_${subdomain}`;

  // Check localStorage cache first for subdomain -> branding profile mapping
  try {
    const cached = localStorage.getItem(subdomainCacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.brandingProfileId) {
        loadBrandingForProfile(parsed.brandingProfileId);
      }
    }
  } catch {
    // Ignore cache errors
  }

  // Fetch subdomain branding profile ID from backend (always fetch to get updates)
  getSubdomainBrandingProfile(subdomain)
    .then(data => {
      const brandingId = (data as any)?.brandingProfileId ?? data?.id;
      if (brandingId) {
        // Cache subdomain -> branding profile mapping
        try {
          localStorage.setItem(subdomainCacheKey, JSON.stringify({ brandingProfileId: brandingId }));
        } catch {
          // localStorage may be unavailable
        }
        loadBrandingForProfile(brandingId);
      }
    })
    .catch(() => {
      // Ignore fetch errors - theme will fall back to default
    });
}

(() => {
  try {
    // First check for upload ID-based branding (download page shareable link)
    const brandingProfileId = extractBrandingProfileIdFromUploadId();
    if (brandingProfileId) {
      loadBrandingForProfile(brandingProfileId);
      return;
    }

    // Then check for subdomain-based branding
    const subdomain = getCurrentSubdomain();
    if (subdomain) {
      loadBrandingForSubdomain(subdomain);
    }
  } catch (err) {
    // Silently fail - branding is not critical
  }
})();
